<template>
  <div>
    <!-- Sticky Top Bar with Breadcrumb -->
    <TopBar
      parentTitle="Ingredients"
      :currentTitle="ingredient.name || 'New Ingredient'"
      :backRoute="{ name: 'IngredientListView' }"
    >
      <b-button outlined type="is-primary" size="is-small" icon-left="check" :disabled="isSaveDisabled" @click="saveAndClose">Save & Close</b-button>
      <b-button type="is-primary" size="is-small" icon-left="content-save" :disabled="isSaveDisabled" @click="save">Save</b-button>
    </TopBar>

    <div class="page-content">
      <!-- Page Header -->
      <div class="page-header">
        <div class="page-title-group">
          <h1 class="page-title">{{ ingredient.name || 'New Ingredient' }}</h1>
          <p class="page-subtitle">Ingredient Editor</p>
        </div>
      </div>

    <!-- Basic Details Card -->
    <FeedsCard title="Basic Details">
      <div class="form-group">
        <label class="form-label">Name</label>
        <b-input
          ref="nameInput"
          v-model="ingredient.name"
          @blur="save"
        />
      </div>
      <div class="form-group">
        <label class="form-label">Ingredient Aisle</label>
        <b-select v-model="selectedAisleId" expanded @input="save">
          <option v-for="aisle in aisles" :value="aisle.id" :key="aisle.id">{{aisle.name}}</option>
        </b-select>
      </div>
      <div class="form-row">
        <div class="form-group">
          <label class="form-label">Item Type</label>
          <b-select v-model="ingredient.item_type" expanded @input="onItemTypeChange">
            <option value="ingredient">Ingredient</option>
            <option value="grocery">Grocery</option>
            <option value="household">Household</option>
          </b-select>
        </div>
        <div class="form-group">
          <label class="form-label">Default Unit</label>
          <b-autocomplete
            v-model="defaultUnitSearch"
            field="term"
            placeholder="e.g. pack, roll, bottle"
            :data="filteredUnits"
            open-on-focus
            @select="selectedUnit"
            @blur="onUnitBlur">
            <template #footer>
              <a @click="openNewUnitModal(defaultUnitSearch)">
                <span>+ Add new</span>
              </a>
            </template>
            <template #empty>
              <span><strong>{{ defaultUnitSearch }}</strong> not found!</span>
            </template>
          </b-autocomplete>
        </div>
      </div>
      <div class="form-group">
        <label class="form-label">
          <div class="inline-toggle">
            <span>Is Food</span>
            <div
              class="toggle"
              :class="{ active: ingredient.is_food }"
              @click="toggleIsFood">
            </div>
          </div>
        </label>
      </div>
    </FeedsCard>

    <!-- Seasonal Display Card -->
    <FeedsCard title="Seasonal Display" class="mt-5">
      <p class="form-hint mb-4">These fields are used when this ingredient appears in seasonal ingredient lists.</p>

      <div class="form-group">
        <label class="form-label">Icon</label>
        <div class="icon-upload-row">
          <div v-if="ingredient.icon_url" class="icon-preview" :style="{ backgroundColor: ingredient.icon_color || '#7CB342' }">
            <img :src="ingredient.icon_url" class="icon-img" />
          </div>
          <div v-else class="icon-placeholder">
            <b-icon icon="image-outline" size="is-medium" />
          </div>
          <label class="button is-primary is-light">
            <b-icon icon="upload"></b-icon>
            <span>{{ ingredient.icon_url ? 'Change Icon' : 'Upload SVG' }}</span>
            <input
              type="file"
              accept="image/svg+xml"
              style="display: none"
              @change="onFileChange"
            />
          </label>
          <b-button
            v-if="ingredient.icon_url"
            type="is-danger"
            icon-left="delete"
            @click="removeIcon">
          </b-button>
        </div>
      </div>

      <div class="form-group">
        <label class="form-label">Icon Color</label>
        <div class="color-input-row">
          <input
            type="color"
            v-model="ingredient.icon_color"
            class="color-picker"
            @change="save"
          />
          <b-input
            v-model="ingredient.icon_color"
            placeholder="#7CB342"
            style="max-width: 120px"
            @blur="save"
          />
        </div>
      </div>
    </FeedsCard>

    <!-- Common Tips Card -->
    <FeedsCard title="Common Tips" class="mt-5">
      <div class="tips-list">
        <div v-for="(tip, index) in ingredient.tips" :key="index" class="tip-item">
          <b-input expanded :value="tip" readonly />
          <b-button type="is-danger" icon-right="delete" @click="removeTip(index)" />
        </div>
        <div class="tip-item new-tip">
          <b-input expanded v-model="newTip" placeholder="e.g. any choi will do" @keyup.enter.native="addNewTip" />
          <b-button type="is-primary" icon-right="plus" :disabled="isAddNewTipDisabled" @click="addNewTip" />
        </div>
      </div>
    </FeedsCard>
    </div>
  </div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid'
import db from "@/firebase/db"
import Storage from "@/firebase/storage"
import FeedsCard from "@/components/common/FeedsCard"
import TopBar from "@/components/layout/TopBar"
import AddNewUnitModal from "@/components/modals/AddNewUnitModal"

export default {
  name: 'IngredientView',
  components: {
    FeedsCard,
    TopBar
  },
  props: [
    'id'
  ],
  data() {
    return {
      aisles: [],
      units: [],
      ingredient: {},
      defaultUnitSearch: '',
      newTip: null,
      isUploadingIcon: false
    };
  },
  firestore: {
    aisles: db.collection('ingredient_aisles').orderBy("position", "asc"),
    units: db.collection('ingredient_units').orderBy("term", "asc"),
  },
  computed: {
    isSaveDisabled () {
      return this.ingredient.name == null || this.ingredient.name.trim() == "" || this.ingredient.aisle == null
    },
    isAddNewTipDisabled () {
      return this.newTip == null
    },
    filteredUnits() {
      if (!this.defaultUnitSearch) return this.units
      return this.units.filter(u =>
        u.term.toLowerCase().indexOf(this.defaultUnitSearch.toLowerCase()) >= 0
      )
    },
    selectedAisleId: {
      get() {
        return this.ingredient.aisle ? this.ingredient.aisle.id : null
      },
      set(id) {
        const match = this.aisles.find(a => a.id === id)
        if (match) {
          this.$set(this.ingredient, 'aisle', {
            id: match.id,
            name: match.name,
            position: match.position
          })
        }
      }
    }
  },
  mounted: function () {
    this.$refs.nameInput.focus();
  },
  created: function () {
    db.collection('ingredients')
      .doc(this.id)
      .get()
      .then(snapshot => {
        this.ingredient = snapshot.data()
        this.defaultUnitSearch = this.ingredient.default_unit || ''
      })
  },
  methods: {
    async saveAndClose(){
      this.save()
        .then(() => {
          this.$router.go(-1)
        })
    },
    async save(){
      if (this.isSaveDisabled) {
        this.$buefy.toast.open({type: 'is-danger', message: `Missing info - Name and Ingredient Aisle must be set!`})
        return;
      }
      db.collection('ingredients')
        .doc(this.id)
        .set(this.ingredient)
        .then(() => {
          this.$buefy.toast.open({type: 'is-success', message: `Ingredient successfully updated!`})
        })
        .catch((error) => {
          this.$buefy.toast.open({type: 'is-danger', message: `Error saving ingredient: ${error}`})
        });
    },
    selectedUnit(option) {
      if (option == null) return
      this.ingredient.default_unit = option.term
      this.defaultUnitSearch = option.term
      this.save()
    },
    onUnitBlur() {
      // If user typed something that doesn't match a unit, clear it
      const match = this.units.find(u => u.term === this.defaultUnitSearch)
      if (!match) {
        this.ingredient.default_unit = null
        this.defaultUnitSearch = ''
      }
      this.save()
    },
    openNewUnitModal(input) {
      const cleansedInput = (input || '').toLowerCase().trim()
      this.$buefy.modal.open({
        parent: this,
        component: AddNewUnitModal,
        hasModalCard: true,
        trapFocus: true,
        props: {
          input: cleansedInput
        },
        events: {
          'unit-added': value => {
            this.ingredient.default_unit = value.term
            this.defaultUnitSearch = value.term
            this.save()
          }
        }
      })
    },
    onItemTypeChange() {
      if (this.ingredient.item_type === 'household') {
        this.ingredient.is_food = false
      } else {
        this.ingredient.is_food = true
      }
      this.save()
    },
    toggleIsFood() {
      this.$set(this.ingredient, 'is_food', !this.ingredient.is_food)
      this.save()
    },
    addNewTip() {
      this.newTip = this.newTip.trim()
      if (!this.newTip || this.newTip == "") { return }
      this.ingredient.tips.push(this.newTip);
      this.save();
      this.newTip = null;
    },
    removeTip(tipIndex) {
      this.$delete(this.ingredient.tips, tipIndex);
      this.save();
    },
    onFileChange(event) {
      const file = event.target.files[0]
      if (file) {
        this.uploadIcon(file)
      }
    },
    uploadIcon(file) {
      if (!file) return

      this.isUploadingIcon = true
      const fileId = uuidv4()
      const ext = file.name.split('.').pop()
      const path = `images/ingredient-icons/${fileId}.${ext}`

      let uploadTask = Storage.ref().child(path).put(file)

      uploadTask.on('state_changed',
        () => {},
        (error) => {
          this.isUploadingIcon = false
          this.$buefy.toast.open({ type: 'is-danger', message: `Error uploading icon: ${error.message}` })
        },
        () => {
          uploadTask.snapshot.ref.getDownloadURL().then((downloadURL) => {
            this.$set(this.ingredient, 'icon_url', downloadURL)
            if (!this.ingredient.icon_color) {
              this.$set(this.ingredient, 'icon_color', '#7CB342')
            }
            this.isUploadingIcon = false
            this.save()
          }).catch((error) => {
            this.$buefy.toast.open({ type: 'is-danger', message: `Error getting download URL: ${error.message}` })
          })
        }
      )
    },
    removeIcon() {
      this.$buefy.dialog.confirm({
        title: 'Remove Icon',
        message: 'Are you sure you want to remove this icon?',
        confirmText: 'Remove',
        type: 'is-danger',
        onConfirm: () => {
          this.$set(this.ingredient, 'icon_url', null)
          this.save()
        }
      })
    }
  }
}
</script>

<style scoped>
.icon-upload-row {
  display: flex;
  align-items: center;
  gap: 16px;
}

.icon-preview {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--border-light);
  padding: 8px;
}

.icon-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  filter: brightness(0) invert(1);
}

.icon-placeholder {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  border: 2px dashed var(--border-medium);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
}

.color-input-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.color-picker {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
  padding: 0;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.inline-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
}

.toggle {
  width: 36px;
  height: 20px;
  background: var(--border-medium);
  border-radius: 10px;
  position: relative;
  cursor: pointer;
  transition: background 0.2s ease;
}

.toggle.active {
  background: var(--feeds-green);
}

.toggle::after {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  background: white;
  border-radius: 50%;
  transition: transform 0.2s ease;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}

.toggle.active::after {
  transform: translateX(16px);
}

.tips-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.tip-item {
  display: flex;
  gap: 8px;
}
</style>
